// ============================================
// COMPONENT - Vista de Certificado Digital
// ============================================

import { useState, useRef } from 'react';
import { useCertificadoSiiStatus, useUploadCertificate, useUIStore, type SiiErrorResponse } from '@almacenes/core';
import { MdCheckCircle, MdError, MdWarning, MdInfo, MdUploadFile, MdClose } from 'react-icons/md';
import { cleanRut, formatRut, validateRut } from '@/hooks/useRutFormatter';

export const CertificadoView = () => {
  const { data: certificado, isLoading, error } = useCertificadoSiiStatus();
  const uploadCertificateMutation = useUploadCertificate();
  const addToast = useUIStore((state) => state.addToast);
  
  const [mostrarModal, setMostrarModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Estados del formulario
  const [archivo, setArchivo] = useState<File | null>(null);
  const [rutRepresentante, setRutRepresentante] = useState('');
  const [rutFormateado, setRutFormateado] = useState('');
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');

  const handleOpenModal = () => {
    setMostrarModal(true);
    resetFormulario();
  };

  const resetFormulario = () => {
    setArchivo(null);
    setRutRepresentante('');
    setRutFormateado('');
    setPassword('');
    setRepeatPassword('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar que sea un archivo PFX
    if (!file.name.toLowerCase().endsWith('.pfx')) {
      addToast('error', 'El archivo debe ser un certificado PFX');
      return;
    }

    setArchivo(file);
  };

  const handleRutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const cleaned = cleanRut(inputValue);
    const limited = cleaned.slice(0, 9);
    setRutRepresentante(limited);
    setRutFormateado(formatRut(limited));
  };

  const handleSubmit = async () => {
    // Validaciones
    if (!archivo) {
      addToast('error', 'Debes seleccionar un archivo de certificado');
      return;
    }

    if (!rutRepresentante || !validateRut(rutRepresentante)) {
      addToast('error', 'Debes ingresar un RUT válido');
      return;
    }

    if (!password) {
      addToast('error', 'Debes ingresar la contraseña del certificado');
      return;
    }

    if (password !== repeatPassword) {
      addToast('error', 'Las contraseñas no coinciden');
      return;
    }

    setIsUploading(true);

    try {
      // Formatear RUT como el backend espera: 12345678-9
      const rut = rutRepresentante.slice(0, -1);
      const dv = rutRepresentante.slice(-1);
      const rutFormateadoBackend = `${rut}-${dv}`;

      await uploadCertificateMutation.mutateAsync({
        file: archivo,
        rutRepresentative: rutFormateadoBackend,
        password,
        repeatPassword,
      });

      addToast('success', 'Certificado subido exitosamente');
      setMostrarModal(false);
      resetFormulario();
    } catch (error: any) {
      // Manejar error 400 con fieldErrors
      if (error.statusCode === 400) {
        const errorData: SiiErrorResponse = error;
        
        if (errorData.fieldErrors && errorData.fieldErrors.length > 0) {
          errorData.fieldErrors.forEach((fieldError: any) => {
            addToast('error', `${fieldError.field}: ${fieldError.message}`);
          });
        } else {
          addToast('error', errorData.message || 'Error al validar el certificado');
        }
      } else {
        addToast('error', error.message || 'Error al subir el certificado. Intenta nuevamente');
      }
      console.error('Error al subir certificado:', error);
    } finally {
      setIsUploading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600">Cargando información del certificado...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <MdError className="text-red-500 text-2xl flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-red-800 font-semibold">Error al cargar certificado</h3>
            <p className="text-red-600 text-sm mt-1">
              No se pudo obtener la información del certificado digital
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!certificado) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <p className="text-gray-600 text-center">No hay información disponible</p>
      </div>
    );
  }

  // Determinar el color según el estado
  const getStatusColor = () => {
    if (!certificado.hasCertificate) return 'gray';
    if (!certificado.isValid) return 'red';
    if (!certificado.isPasswordCorrect) return 'orange';
    if (certificado.daysRemaining <= 30) return 'yellow';
    return 'green';
  };

  const statusColor = getStatusColor();

  const colorClasses = {
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-800',
      icon: 'text-green-500',
    },
    yellow: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      text: 'text-yellow-800',
      icon: 'text-yellow-500',
    },
    orange: {
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      text: 'text-orange-800',
      icon: 'text-orange-500',
    },
    red: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-800',
      icon: 'text-red-500',
    },
    gray: {
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      text: 'text-gray-800',
      icon: 'text-gray-500',
    },
  };

  const colors = colorClasses[statusColor];

  const getStatusIcon = () => {
    if (!certificado.hasCertificate) return <MdInfo className={`text-4xl ${colors.icon}`} />;
    if (!certificado.isValid) return <MdError className={`text-4xl ${colors.icon}`} />;
    if (!certificado.isPasswordCorrect) return <MdWarning className={`text-4xl ${colors.icon}`} />;
    if (certificado.daysRemaining <= 30) return <MdWarning className={`text-4xl ${colors.icon}`} />;
    return <MdCheckCircle className={`text-4xl ${colors.icon}`} />;
  };

  const getStatusMessage = () => {
    if (!certificado.hasCertificate) return 'No hay certificado registrado';
    if (!certificado.isValid) return 'Certificado inválido';
    if (!certificado.isPasswordCorrect) return 'Contraseña del certificado incorrecta';
    if (certificado.daysRemaining <= 30) return 'Certificado próximo a vencer';
    return 'Certificado válido y operativo';
  };

  return (
    <div className="space-y-6">
      {/* Botón para subir certificado */}
      <div className="flex justify-end">
        <button
          onClick={handleOpenModal}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          <MdUploadFile className="text-xl" />
          <span>Subir Certificado</span>
        </button>
      </div>

      {/* Estado General */}
      <div className={`${colors.bg} border ${colors.border} rounded-lg p-6`}>
        <div className="flex items-start gap-4">
          {getStatusIcon()}
          <div className="flex-1">
            <h3 className={`text-xl font-bold ${colors.text}`}>
              {getStatusMessage()}
            </h3>
            <p className={`text-sm mt-1 ${colors.text} opacity-80`}>
              Estado del certificado digital para emisión de documentos tributarios
            </p>
          </div>
        </div>
      </div>

      {/* Detalles del Certificado */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900">Detalles del Certificado</h4>
        </div>
        
        <div className="divide-y divide-gray-200">
          {/* Tiene Certificado */}
          <div className="px-6 py-4 flex justify-between items-center">
            <div>
              <p className="font-medium text-gray-900">Certificado Registrado</p>
              <p className="text-sm text-gray-500 mt-1">
                Indica si existe un certificado digital cargado en el sistema
              </p>
            </div>
            <div className="flex items-center gap-2">
              {certificado.hasCertificate ? (
                <>
                  <MdCheckCircle className="text-green-500 text-xl" />
                  <span className="font-semibold text-green-700">Sí</span>
                </>
              ) : (
                <>
                  <MdError className="text-red-500 text-xl" />
                  <span className="font-semibold text-red-700">No</span>
                </>
              )}
            </div>
          </div>

          {/* Es Válido */}
          <div className="px-6 py-4 flex justify-between items-center">
            <div>
              <p className="font-medium text-gray-900">Estado de Validez</p>
              <p className="text-sm text-gray-500 mt-1">
                Certificado vigente y no expirado
              </p>
            </div>
            <div className="flex items-center gap-2">
              {certificado.isValid ? (
                <>
                  <MdCheckCircle className="text-green-500 text-xl" />
                  <span className="font-semibold text-green-700">Válido</span>
                </>
              ) : (
                <>
                  <MdError className="text-red-500 text-xl" />
                  <span className="font-semibold text-red-700">Inválido</span>
                </>
              )}
            </div>
          </div>

          {/* Contraseña Correcta */}
          <div className="px-6 py-4 flex justify-between items-center">
            <div>
              <p className="font-medium text-gray-900">Contraseña del Certificado</p>
              <p className="text-sm text-gray-500 mt-1">
                Verifica que la contraseña configurada sea correcta
              </p>
            </div>
            <div className="flex items-center gap-2">
              {certificado.isPasswordCorrect ? (
                <>
                  <MdCheckCircle className="text-green-500 text-xl" />
                  <span className="font-semibold text-green-700">Correcta</span>
                </>
              ) : (
                <>
                  <MdWarning className="text-orange-500 text-xl" />
                  <span className="font-semibold text-orange-700">Incorrecta</span>
                </>
              )}
            </div>
          </div>

          {/* Fecha de Vencimiento */}
          <div className="px-6 py-4 flex justify-between items-center">
            <div>
              <p className="font-medium text-gray-900">Fecha de Vencimiento</p>
              <p className="text-sm text-gray-500 mt-1">
                Día en que el certificado expira
              </p>
            </div>
            <div className="text-right">
              <p className="font-semibold text-gray-900">
                {new Date(certificado.expirationDate + 'T00:00:00').toLocaleDateString('es-CL', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
          </div>

          {/* Días Restantes */}
          <div className="px-6 py-4 flex justify-between items-center">
            <div>
              <p className="font-medium text-gray-900">Días Restantes</p>
              <p className="text-sm text-gray-500 mt-1">
                Tiempo antes de que expire el certificado
              </p>
            </div>
            <div className="text-right">
              <p className={`text-2xl font-bold ${
                certificado.daysRemaining <= 30 ? 'text-orange-600' : 
                certificado.daysRemaining <= 60 ? 'text-yellow-600' : 
                'text-green-600'
              }`}>
                {certificado.daysRemaining}
              </p>
              <p className="text-sm text-gray-500">días</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recomendaciones */}
      {(!certificado.hasCertificate || !certificado.isValid || !certificado.isPasswordCorrect || certificado.daysRemaining <= 30) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <MdInfo className="text-blue-500 text-2xl flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-blue-900 font-semibold mb-2">Recomendaciones</h4>
              <ul className="text-blue-800 text-sm space-y-2">
                {!certificado.hasCertificate && (
                  <li>• Debes registrar un certificado digital para emitir documentos tributarios electrónicos</li>
                )}
                {!certificado.isValid && (
                  <li>• El certificado ha expirado o es inválido. Renueva tu certificado digital</li>
                )}
                {!certificado.isPasswordCorrect && (
                  <li>• Verifica y actualiza la contraseña del certificado en la configuración</li>
                )}
                {certificado.daysRemaining <= 30 && (
                  <li>• El certificado está próximo a vencer. Considera renovarlo pronto para evitar interrupciones</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de Subir Certificado */}
      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">
                Subir Certificado Digital
              </h2>
              <button
                onClick={() => !isUploading && setMostrarModal(false)}
                disabled={isUploading}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                <MdClose className="text-3xl text-gray-600" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-6 space-y-4">
              {/* Archivo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Archivo Certificado (.pfx) <span className="text-red-500">*</span>
                </label>
                
                {/* Input oculto */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pfx"
                  onChange={handleFileChange}
                  disabled={isUploading}
                  className="hidden"
                />
                
                {/* Botón personalizado */}
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 group"
                  >
                    <MdUploadFile className="text-2xl text-gray-400 group-hover:text-blue-500 transition-colors" />
                    <span className="text-gray-600 group-hover:text-blue-600 font-medium transition-colors">
                      {archivo ? 'Cambiar archivo' : 'Seleccionar archivo .pfx'}
                    </span>
                  </button>
                  
                  {archivo && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                      <MdCheckCircle className="text-green-600 text-lg flex-shrink-0" />
                      <span className="text-sm text-green-700 font-medium truncate">
                        {archivo.name}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* RUT Representante */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  RUT Representante Legal <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={rutFormateado}
                  onChange={handleRutChange}
                  disabled={isUploading}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="12.345.678-9"
                  maxLength={12}
                />
                {rutRepresentante && !validateRut(rutRepresentante) && (
                  <p className="text-xs text-red-500 mt-1">RUT inválido</p>
                )}
              </div>

              {/* Contraseña */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contraseña del Certificado <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isUploading}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Contraseña"
                />
              </div>

              {/* Repetir Contraseña */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Repetir Contraseña <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={repeatPassword}
                  onChange={(e) => setRepeatPassword(e.target.value)}
                  disabled={isUploading}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Repetir contraseña"
                />
                {password && repeatPassword && password !== repeatPassword && (
                  <p className="text-xs text-red-500 mt-1">Las contraseñas no coinciden</p>
                )}
              </div>

              {/* Botón Submit */}
              <button
                onClick={handleSubmit}
                disabled={
                  isUploading ||
                  !archivo ||
                  !rutRepresentante ||
                  !validateRut(rutRepresentante) ||
                  !password ||
                  !repeatPassword ||
                  password !== repeatPassword
                }
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isUploading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Subiendo...</span>
                  </>
                ) : (
                  <>
                    <MdUploadFile className="text-xl" />
                    Subir Certificado
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
